import React, { useEffect, useRef, useState, ReactNode } from "react";

type Direction = "up" | "down" | "left" | "right" | "none";

interface RevealProps {
  children: ReactNode;
  className?: string;
  as?: keyof JSX.IntrinsicElements;
  threshold?: number;
  rootMargin?: string;
  direction?: Direction;
  delay?: number; // milliseconds
}

const directionClass = (dir: Direction) => {
  switch (dir) {
    case "up":
      return "translate-y-6";
    case "down":
      return "-translate-y-6";
    case "left":
      return "translate-x-6";
    case "right":
      return "-translate-x-6";
    default:
      return "";
  }
};

const Reveal: React.FC<RevealProps> = ({
  children,
  className = "",
  as = "div",
  threshold = 0.2,
  rootMargin = "0px 0px -10% 0px",
  direction = "up",
  delay = 0,
}) => {
  const Tag = as as any;
  const ref = useRef<HTMLDivElement | null>(null);
  const [visibleOnce, setVisibleOnce] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleOnce(true);
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  const base = "transition-all duration-700 ease-out will-change-transform";
  const hidden = `opacity-0 ${directionClass(direction)}`;
  const shown = "opacity-100 translate-x-0 translate-y-0";

  return (
    <Tag
      ref={ref}
      className={`${base} ${visibleOnce ? shown : hidden} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </Tag>
  );
};

export default Reveal;
